import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";
import { Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useMemo, useState } from "react";
import unitService from "services/units-service";
import useOpen from "utils/useOpen";
import UnitsDialog from "./UnitsDialog";

export default function AllUnits({ permissions }) {
  const { label } = useLanguage();
  const open = useOpen({ manageDialog: false, alert: false });
  const can = usePermissionMonitor();

  const [units, setUnits] = useState(null);
  const [lastFilter, setLastFilter] = useState(null);

  async function handleAddUnit(newCategory) {
    const [response, data] = await unitService.createUnit(newCategory);
    if (response.ok) {
      const [response, data] = await unitService.getUnits(lastFilter);
      if (response.ok) {
        setUnits(data);
      }
      open.change("create", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateUnit(newCategory) {
    const [response, data] = await unitService.updateUnit(newCategory.id, {
      ...newCategory,
      options: newCategory.options
        ? newCategory.options.map((option) => {
            return {
              name: option.label,
              sequence: option.sequence,
              value: option.id,
            };
          })
        : null,
    });
    if (response.ok) {
      const [response, data] = await unitService.getUnits(lastFilter);
      if (response.ok) {
        setUnits(data);
      }
      open.change("update", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteUnit(id) {
    const [response, data] = await unitService.removeUnit(id);
    if (response.ok) {
      const [response, data] = await unitService.getUnits(lastFilter);
      if (response.ok) {
        setUnits(data);
      }
      open.change("alert", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  const rows = useMemo(() => {
    if (!units?.length)
      return units?.length === 0
        ? [
            {
              name: label("no-units-were-found"),
            },
          ]
        : [
            {
              name: "Loading...",
            },
          ];
    return units.map((unit) => {
      return {
        name: <MDTypography variant="inherit">{unit.name}</MDTypography>,
        referenceCode: (
          <MDTypography variant="inherit">
            {unit.referenceCode || label("none")}
          </MDTypography>
        ),
        entity: (
          <MDTypography variant="inherit">
            {unit.entity?.name || "—"}
          </MDTypography>
        ),
        serialNumber: (
          <MDTypography variant="inherit">
            {unit.serialNumber || "—"}
          </MDTypography>
        ),
        account: (
          <MDTypography variant="inherit">
            {unit.account?.name || label("all")}
          </MDTypography>
        ),

        actions:
          (!unit.accountId && can("isAdmin")) || unit.accountId ? (
            <MDBox
              display="flex"
              flexDirection="row"
              justifyContent="end"
              alignItems="center"
            >
              {permissions.update && (
                <Tooltip title={label("Edit")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("update", {
                        unit,
                      });
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {permissions.delete && (
                <Tooltip title={label("Delete")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("alert", {
                        unit,
                      });
                    }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          ) : null,
      };
    });
  }, [units, permissions]);

  const columns = useMemo(() => {
    const columns = [
      {
        Header: label("name-singular"),
        accessor: "name",
      },
      {
        Header: label("account-singular"),
        accessor: "account",
      },
    ];

    if (permissions.update || permissions.delete) {
      columns.push({
        Header: "",
        accessor: "actions",
        align: "right",
      });
    }

    return columns;
  }, [permissions]);

  return (
    <>
      {open.get("alert") ? (
        <AlertDialog
          open={Math.random()}
          setOpen={(v) => {
            open.change("alert", v);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await handleDeleteUnit(open.get("alert").unit.id);
          }}
        />
      ) : null}
      {open.get("create") ? (
        <UnitsDialog
          mode="create"
          isOpen={open.get("create") ? true : false}
          onSubmit={handleAddUnit}
          onClose={() => open.change("create", false)}
          data={null}
        />
      ) : null}
      {open.get("update") ? (
        <UnitsDialog
          mode="edit"
          isOpen={open.get("update") ? true : false}
          onSubmit={handleUpdateUnit}
          onClose={() => open.change("update", false)}
          data={open.get("update").unit}
        />
      ) : null}

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("unit-plural")}{" "}
                  {permissions.get ? `(${units?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("create", true);
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-unit-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-units"
                      internalLoading={!Boolean(units)}
                      fullWidth
                      setData={setUnits}
                      onSearch={async (request, callback) => {
                        setLastFilter(request);
                        callback(await unitService.getUnits(request));
                      }}
                      label={label("search-units-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(units)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
